// Properties Demo Steps Configuration
// This file contains all demo steps for pages under /Pages/Properties/

export const propertiesIndexSteps = [
  {
    title: "Welcome to Tenant Screening!",
    content: `
      <p class="mb-3">Welcome to your Tenant Screening dashboard! This is where you can manage tenant screening and enhanced screening your rental properties.</p>
      <p>We'll give you a quick tour of the key features to help you get started. Click <strong>Next</strong> to continue.</p>
    `,
    info: "This tour will only take a minute and will help you understand how to use this page effectively.",
    target: null
  },
  {
    title: "Add New Property",
    content: `
      <p class="mb-3">This <strong>Add Property</strong> button is your starting point for adding new rental properties, getting a shareable property specific application link and to begin screening applicants.</p>
      <p>Click here whenever you want to list a new property and are ready to start screening applicants.</p>
    `,
    info: "Pro tip: You can add properties for both rental management and insurance quotes from this same button.",
    target: "add-property-btn",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Properties Table",
    content: `
      <p class="mb-3">This table shows all your properties with key information at a glance:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Address:</strong> Property location</li>
        <li><strong>Rent:</strong> Monthly rental amount</li>
        <li><strong>Applications:</strong> Number of active applications</li>
        <li><strong>Status:</strong> Whether the property is active or archived</li>
      </ul>
    `,
    info: "You can sort columns by clicking on the headers, and use the search box to find specific properties.",
    target: "properties-table"
  },
  {
    title: "Download Applications",
    content: `
      <p class="mb-3">The <strong>Download All Applicants</strong> button lets you export application data for all your properties.</p>
      <p>This is useful for:</p>
      <ul class="list-disc ml-5 mt-2 space-y-1">
        <li>Backing up applicant information</li>
        <li>Reviewing applications offline</li>
        <li>Sharing data with property managers</li>
      </ul>
    `,
    info: "The download includes all application details in a convenient spreadsheet format.",
    target: "download-applicants-btn",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "You're All Set!",
    content: `
      <p class="mb-3">🎉 <strong>Congratulations!</strong> You've completed the Tenant Screening Dashboard tour.</p>
      <p class="mb-3">You now know how to:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Add new properties</li>
        <li>View and manage your property list</li>
        <li>Download application data</li>
      </ul>
      <p class="mt-3">Click <strong>Complete</strong> to finish the tour. You can always access this help again using the help button.</p>
    `,
    info: "Need help later? Look for the blue help button (?) in the bottom-right corner of any page.",
    target: null
  }
]

export const propertiesCreateSteps = [
  {
    title: "Welcome to Add Property!",
    content: `
      <p class="mb-3">Welcome to the Add a Property form! This is where you'll enter details about your rental property in order to screen an applicant.</p>
      <p>We'll walk you through each section to help you create your property listing successfully. Click <strong>Next</strong> to continue.</p>
    `,
    info: "This tour will guide you through all the form sections including address lookup, property details, and payment settings.",
    target: null
  },
  {
    title: "Address Lookup",
    content: `
      <p class="mb-3">Start by typing your property's address in this <strong>Address Lookup</strong> field.</p>
      <p class="mb-3">We use Google Maps to automatically fill in the address details. Key features:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Start typing and select from the dropdown suggestions</li>
        <li>Include the postal code for best accuracy</li>
        <li>All address fields will be filled automatically except Address 2</li>
      </ul>
    `,
    info: "If you experience issues with address lookup, contact support via the chat window below.",
    target: "address_lookup",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Owner/Landlord Name",
    content: `
      <p class="mb-3">Enter the <strong>Owner/Landlord Name</strong> for this property.</p>
      <p>This is the name that will appear on rental applications and correspondence with tenants.</p>
    `,
    info: "This field is optional but recommended for professional property management.",
    target: "owner_name",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Monthly Rent",
    content: `
      <p class="mb-3">Set the <strong>Monthly Rent</strong> amount for this property.</p>
      <p>This amount will be displayed to potential tenants and used for application processing.</p>
    `,
    info: "Enter the full monthly rent amount. You can include cents if needed (For Example, 1200.50).",
    target: "rent",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Availability Date",
    content: `
      <p class="mb-3">Select the <strong>Availability Date</strong> when the property will be ready for occupancy.</p>
      <p>This helps tenants know when they can move in and filters applications accordingly.</p>
    `,
    info: "This is a required field - make sure to set a realistic availability date.",
    target: "availability_date",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Accept Applications",
    content: `
      <p class="mb-3">Choose whether you're ready to <strong>Accept Applications</strong> today.</p>
      <p class="mb-3">Your options:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>YES:</strong> Start accepting applications immediately</li>
        <li><strong>NO:</strong> Add the property but don't accept applications yet</li>
      </ul>
    `,
    info: "You can always change this setting later from your properties dashboard.",
    target: "status-yes",
    modalOffset: { x: 40, y: 0 }
  },
  {
    title: "Application Fee Payment",
    content: `
      <p class="mb-3">Select who will pay the $59 <strong>Application Fee</strong>.</p>
      <p class="mb-3">Options:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Applicant pays:</strong> Fee is charged directly to the applicant</li>
        <li><strong>You pay:</strong> You pay the fee on behalf of applicants</li>
      </ul>
    `,
    info: "If you choose to pay the fee, you'll need to add a payment method to your account.",
    target: "applicant-fee-yes",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Rent Payments",
    content: `
      <p class="mb-3">Decide if you want to collect <strong>Rent Payments</strong> online through RentRisk.</p>
      <p class="mb-3">Benefits of online rent collection:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Automatic monthly payments</li>
        <li>Direct deposit to your account</li>
        <li>Digital payment tracking</li>
      </ul>
    `,
    info: "Online rent collection makes managing payments much easier for both you and your tenants.",
    target: "rent-payments-yes",
    modalOffset: { x: 40, y: 0 }
  },
  {
    title: "Save Your Property",
    content: `
      <p class="mb-3">Once you've completed all sections, click the <strong>Save</strong> button to create your property listing.</p>
      <p>The system will validate your information and create the property in your account.</p>
    `,
    info: "Make sure all required fields are filled before saving. You can always edit the property details later.",
    target: "save-button",
    modalOffset: { x: -40, y: -40 }
  },
  {
    title: "You're All Set!",
    content: `
      <p class="mb-3">🎉 <strong>Congratulations!</strong> You now know how to add properties to your account.</p>
      <p class="mb-3">Key things you learned:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>How to use address lookup</li>
        <li>Setting rent and availability dates</li>
        <li>Configuring application and payment settings</li>
        <li>Managing renter's insurance requirements</li>
      </ul>
      <p class="mt-3">Click <strong>Complete</strong> to finish the tour.</p>
    `,
    info: "Remember: You can access this help again anytime using the help button in the bottom-right corner.",
    target: null
  }
]

export const propertiesEditSteps = [
  {
    title: "Welcome to Edit Property!",
    content: `
      <p class="mb-3">This is where you can modify your existing property details.</p>
      <p>You can update address information, rent amounts, availability dates, and all other property settings.</p>
    `,
    info: "Changes are saved automatically when you click the Save button.",
    target: null
  },
  {
    title: "Property Address",
    content: `
      <p class="mb-3">Update your property's <strong>address information</strong> if needed.</p>
      <p>The address lookup works the same as when creating a property - start typing and select from suggestions.</p>
    `,
    info: "Address changes may affect your property's visibility to potential tenants.",
    target: "address_lookup"
  },
  {
    title: "Update Rent Amount",
    content: `
      <p class="mb-3">Modify the <strong>Monthly Rent</strong> for this property.</p>
      <p>This change will be reflected in any new applications but won't affect existing applications.</p>
    `,
    info: "Consider market rates when adjusting rent prices.",
    target: "rent"
  },
  {
    title: "Availability Settings",
    content: `
      <p class="mb-3">Update when the property will be <strong>available for occupancy</strong>.</p>
      <p>This helps manage tenant expectations and application timing.</p>
    `,
    info: "Setting a future availability date can help you plan tenant transitions.",
    target: "availability_date"
  },
  {
    title: "Save Changes",
    content: `
      <p class="mb-3">Click <strong>Save</strong> to apply all your changes to the property.</p>
      <p>Your updates will be immediately reflected across the system.</p>
    `,
    info: "Always update your changes before navigating away from this page.",
    target: "save-button",
    modalOffset: { x: -40, y: -40 }
  }
]

export const propertiesShowSteps = [
  {
    title: "Property Details Overview",
    content: `
      <p class="mb-3">This page shows comprehensive details about your selected property.</p>
      <p>Here you can view applications, manage settings, and access property-specific tools.</p>
    `,
    info: "This is your central hub for managing individual properties.",
    target: null
  },
  {
    title: "Property Information",
    content: `
      <p class="mb-3">View complete <strong>property details</strong> including address, rent, and status.</p>
      <p>This information is what potential tenants see when they view your application.</p>
    `,
    info: "You can edit these details by clicking the Edit Property button.",
  },
  {
    title: "Property Link",
    content: `
      <p class="mb-3">
        Use the <strong>Copy Link</strong> button to share this property's unique application link.
      </p>
      <p>
        Send it to tenants by text or email, or add it to your listing so applicants can easily apply online.
      </p>
    `,
    target: "copy-link-button",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Applications List",
    content: `
      <p class="mb-3">See all <strong>applications submitted</strong> for this property.</p>
      <p>Review, approve, or reject applications directly from this view.</p>
    `,
    info: "Applications are sorted by submission date, with newest first.",
    target: "applications-list"
  },
  {
    title: "Payment Status",
    content: `
      <p class="mb-3">This shows whether the applicant has submitted payment for the application.</p>
      <p>Keep track of pending payments to follow up with applicants easily.</p>
    `,
    target: "widget-paid",
  },

  {
    title: "Report Shared",
    content: `
      <p class="mb-3">This indicates if the applicant has completed and shared their report with you.</p>
      <p>Use this to monitor which applicants have finished their verification steps.</p>
    `,
    target: "widget-report-shared",
  },

  {
    title: "Financial Documents",
    content: `
      <p class="mb-3">Shows whether the applicant has uploaded their required financial documents.</p>
      <p>Quickly see which documents are missing and follow up if needed.</p>
    `,
    target: "widget-financial-docs",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Enhanced Screening",
    content: `
      <p class="mb-3">With Enhanced Screening, you can request additional verifications from applicants to get a clearer picture of their profile:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Identity Verification</li>
        <li>Income Verification</li>
        <li>Assets Verification</li>
        <li>Employment Verification</li>
      </ul>
      <p class="mt-3">This helps you make more confident and reliable screening decisions.</p>
    `,
    target: "enhanced-screening-button",
    modalOffset: { x: -40, y: -20 }
  },
  {
    title: "Property Actions",
    content: `
      <p class="mb-3">Use these action buttons to:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Edit property details</li>
        <li>Share the application link</li>
        <li>Download application data</li>
        <li>Archive the property</li>
      </ul>
    `,
    target: "property-actions",
    info: "These tools help you screen applicants efficiently.",
    modalOffset: { x: -40, y: 0 }
  }
]

export const propertiesApplicationSteps = [
  {
    title: "Welcome to Property Application!",
    content: `
      <p class="mb-3">This is the tenant-facing application form for your property.</p>
      <p>Let's walk through what tenants will see and fill out when applying.</p>
    `,
    info: "Understanding the tenant experience helps you better support applicants.",
    target: null
  },
  {
    title: "Application Form",
    content: `
      <p class="mb-3">Tenants fill out their <strong>personal information</strong> including:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Contact details</li>
        <li>Employment information</li>
        <li>Income verification</li>
        <li>References</li>
      </ul>
    `,
    info: "All information is securely collected and stored.",
    target: "application-form"
  },
  {
    title: "Document Upload",
    content: `
      <p class="mb-3">Applicants can <strong>upload required documents</strong> such as:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Pay stubs</li>
        <li>Bank statements</li>
        <li>ID verification</li>
        <li>References</li>
      </ul>
    `,
    info: "Documents are automatically organized and stored securely.",
    target: "document-upload"
  },
  {
    title: "Payment Processing",
    content: `
      <p class="mb-3">If configured, tenants complete <strong>application fee payment</strong> here.</p>
      <p>The system handles all payment processing securely.</p>
    `,
    info: "Payment confirmations are sent automatically to both you and the tenant.",
    target: "payment-section"
  }
]

export const propertiesApplicationDetailsSteps = [
  {
    title: "Application Details View",
    content: `
      <p class="mb-3">This page shows complete details for a specific tenant application.</p>
      <p>Review all submitted information and make approval decisions here.</p>
    `,
    info: "All applicant data is presented in an organized, easy-to-review format.",
    target: null
  },
  {
    title: "Applicant Information",
    content: `
      <p class="mb-3">Review the applicant's <strong>personal and contact information</strong>.</p>
      <p>This includes their name, phone, email, and current address.</p>
    `,
    info: "Verify contact information is complete and accurate.",
    target: "applicant-info"
  },
  {
    title: "Employment Details",
    content: `
      <p class="mb-3">View <strong>employment and income information</strong> provided by the applicant.</p>
      <p>This helps you assess their financial ability to pay rent.</p>
    `,
    info: "Cross-reference with uploaded documents for verification.",
    target: "employment-details"
  },
  {
    title: "Credit and Background",
    content: `
      <p class="mb-3">Access <strong>credit reports and background checks</strong> if ordered.</p>
      <p>These provide crucial insights for your rental decision.</p>
    `,
    info: "Reports are available immediately after processing.",
    target: "credit-background"
  },
  {
    title: "Application Actions",
    content: `
      <p class="mb-3">Take action on this application:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Approve:</strong> Accept the tenant</li>
        <li><strong>Reject:</strong> Decline the application</li>
        <li><strong>Request Info:</strong> Ask for additional details</li>
      </ul>
    `,
    info: "Your decision is communicated automatically to the applicant.",
    target: "application-actions"
  }
]

export const propertiesGuestApplicationSteps = [
  {
    title: "Guest Application Process",
    content: `
      <p class="mb-3">This is the public application form that potential tenants access via your property link.</p>
      <p>Let's review how the guest application process works.</p>
    `,
    info: "This form is optimized for ease of use and maximum completion rates.",
    target: null
  },
  {
    title: "Property Information Display",
    content: `
      <p class="mb-3">Potential tenants see your <strong>property details</strong> at the top:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Property address</li>
        <li>Monthly rent amount</li>
        <li>Availability date</li>
        <li>Property features</li>
      </ul>
    `,
    info: "Clear property information helps attract qualified applicants.",
    target: "property-display"
  },
  {
    title: "Application Form Fields",
    content: `
      <p class="mb-3">Tenants complete <strong>all required information</strong> in this form.</p>
      <p>The form guides them through each step with clear labels and validation.</p>
    `,
    info: "Form validation ensures complete and accurate submissions.",
    target: "guest-form"
  },
  {
    title: "Submission Process",
    content: `
      <p class="mb-3">After completing the form, applicants <strong>submit their application</strong>.</p>
      <p>You'll receive immediate notification of new applications.</p>
    `,
    info: "Applicants receive a confirmation email with their application details.",
    target: "submit-application"
  }
]